import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query'
import {
  createKnowledgeSource,
  deleteKnowledgeSource,
  deleteKnowledgeSourceDocument,
  getKnowledgeSource,
  getKnowledgeSourceDocumentContent,
  listCompanyWideKnowledgeSources,
  listKnowledgeSourceDocuments,
  listKnowledgeSources,
  listTeamworkSpaces,
  refreshKnowledgeSource,
  resyncFailedKnowledgeSourceDocuments,
  updateKnowledgeSource,
  uploadKnowledgeSourceDocuments,
  type CorpusDeleteResponse,
  type CorpusResponse,
  type CorpusSyncStatus,
  type CreateCorpusPayload,
  type DocumentContentResponse,
  type DocumentInCorpus,
  type DocumentSyncStatus,
  type TeamworkSpace,
  type UpdateCorpusPayload
} from 'shared/api/copilot'
import { ApiClientError } from 'shared/api/fetch'

export const KNOWLEDGE_SOURCES_QUERY_KEY = 'copilot-knowledge-sources'
export const COMPANY_WIDE_KNOWLEDGE_SOURCES_QUERY_KEY = 'copilot-company-wide-knowledge-sources'
export const KNOWLEDGE_SOURCE_QUERY_KEY = 'copilot-knowledge-source'
export const KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY = 'copilot-knowledge-source-documents'
export const KNOWLEDGE_SOURCE_DOCUMENT_CONTENT_QUERY_KEY = 'copilot-knowledge-source-document-content'
export const TEAMWORK_SPACES_QUERY_KEY = 'copilot-teamwork-spaces-v2'

const TEAMWORK_SPACES_STALE_MS = 5 * 60 * 1000
const TEAMWORK_SPACES_MAX_RETRIES = 2
const HTTP_CLIENT_ERROR_MIN = 400
const HTTP_CLIENT_ERROR_MAX = 500

const IN_PROGRESS_REFETCH_MS = 5000

export const shouldRetryTeamworkSpaces = (failureCount: number, error: Error): boolean => {
  const status = error instanceof ApiClientError ? error.status_code : undefined
  if (status !== undefined && status >= HTTP_CLIENT_ERROR_MIN && status < HTTP_CLIENT_ERROR_MAX) {
    return false
  }
  return failureCount < TEAMWORK_SPACES_MAX_RETRIES
}

export const useKnowledgeSources = (clientId: number, enabled = true): UseQueryResult<CorpusResponse[]> => {
  return useQuery({
    queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId],
    queryFn: async () => await listKnowledgeSources(clientId),
    enabled: enabled && Boolean(clientId),
    retry: false,
    refetchInterval: (query) => {
      const corpora = query.state.data ?? []
      return corpora.some((corpus) => corpus.syncStatus === 'in_progress') ? IN_PROGRESS_REFETCH_MS : false
    }
  })
}

export const useCompanyWideKnowledgeSources = (clientId: number, enabled = true): UseQueryResult<CorpusResponse[]> => {
  return useQuery({
    queryKey: [COMPANY_WIDE_KNOWLEDGE_SOURCES_QUERY_KEY, clientId],
    queryFn: async () => await listCompanyWideKnowledgeSources(clientId),
    enabled: enabled && Boolean(clientId),
    retry: false
  })
}

export const useKnowledgeSource = (
  clientId: number,
  corpusId: string | undefined,
  enabled = true
): UseQueryResult<CorpusResponse> => {
  return useQuery({
    queryKey: [KNOWLEDGE_SOURCE_QUERY_KEY, clientId, corpusId],
    queryFn: async () => await getKnowledgeSource(clientId, corpusId as string),
    enabled: enabled && Boolean(clientId) && corpusId != null && corpusId !== '',
    retry: false,
    refetchInterval: (query) => (query.state.data?.syncStatus === 'in_progress' ? IN_PROGRESS_REFETCH_MS : false)
  })
}

export const useCreateKnowledgeSource = (
  clientId: number
): UseMutationResult<CorpusResponse, Error, CreateCorpusPayload> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload) => await createKnowledgeSource(clientId, payload),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })

      // Connected sources need an initial sync; manual-only corpora wait for uploads.
      if (created.sourceConfigs.length > 0) {
        try {
          await refreshKnowledgeSource(clientId, created.id)
        } catch {
          // Create already succeeded — user can resync from the more menu.
        }

        await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
        await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCE_QUERY_KEY, clientId, created.id] })
        await queryClient.invalidateQueries({
          queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, created.id]
        })
      }
    }
  })
}

export const useUpdateKnowledgeSource = (
  clientId: number
): UseMutationResult<CorpusResponse, Error, { corpusId: string; payload: UpdateCorpusPayload }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ corpusId, payload }) => await updateKnowledgeSource(clientId, corpusId, payload),
    onSuccess: async (data, { corpusId }) => {
      queryClient.setQueryData([KNOWLEDGE_SOURCE_QUERY_KEY, clientId, corpusId], data)
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
      await queryClient.invalidateQueries({
        queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, corpusId]
      })
    }
  })
}

export const useDeleteKnowledgeSource = (
  clientId: number
): UseMutationResult<CorpusDeleteResponse, Error, { corpusId: string }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ corpusId }) => await deleteKnowledgeSource(clientId, corpusId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
    }
  })
}

export const useKnowledgeSourceDocuments = (
  clientId: number,
  corpusId: string | undefined,
  params?: { syncStatus?: DocumentSyncStatus; limit?: number; page?: number; perPage?: number },
  enabled = true,
  options?: { corpusSyncStatus?: CorpusSyncStatus }
): UseQueryResult<DocumentInCorpus[]> => {
  const corpusSyncStatus = options?.corpusSyncStatus
  const isCorpusSyncing = corpusSyncStatus === 'in_progress' || corpusSyncStatus === 'pending'

  return useQuery({
    queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, corpusId, params],
    queryFn: async () => await listKnowledgeSourceDocuments(clientId, corpusId as string, params),
    enabled: enabled && Boolean(clientId) && corpusId != null && corpusId !== '',
    retry: false,
    refetchInterval: enabled && isCorpusSyncing ? IN_PROGRESS_REFETCH_MS : false
  })
}

export const useKnowledgeSourceDocumentContent = (
  clientId: number,
  corpusId: string | undefined,
  documentId: string | undefined,
  enabled = true
): UseQueryResult<DocumentContentResponse> => {
  return useQuery({
    queryKey: [KNOWLEDGE_SOURCE_DOCUMENT_CONTENT_QUERY_KEY, clientId, corpusId, documentId],
    queryFn: async () => await getKnowledgeSourceDocumentContent(clientId, corpusId as string, documentId as string),
    enabled: enabled && Boolean(clientId) && corpusId != null && documentId != null,
    retry: false
  })
}

export const useUploadKnowledgeSourceDocuments = (
  clientId: number
): UseMutationResult<DocumentInCorpus[], Error, { corpusId: string; files: File[] }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ corpusId, files }) => await uploadKnowledgeSourceDocuments(clientId, corpusId, files),
    onSuccess: async (_data, { corpusId }) => {
      await queryClient.invalidateQueries({
        queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, corpusId]
      })
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCE_QUERY_KEY, clientId, corpusId] })
    }
  })
}

export const useDeleteKnowledgeSourceDocument = (
  clientId: number
): UseMutationResult<void, Error, { corpusId: string; documentId: string }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ corpusId, documentId }) => {
      await deleteKnowledgeSourceDocument(clientId, corpusId, documentId)
    },
    onSuccess: async (_data, { corpusId }) => {
      await queryClient.invalidateQueries({
        queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, corpusId]
      })
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
    }
  })
}

export const useRefreshKnowledgeSource = (
  clientId: number
): UseMutationResult<CorpusResponse, Error, { corpusId: string; force?: boolean; fullRefresh?: boolean }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ corpusId, force, fullRefresh }) =>
      await refreshKnowledgeSource(clientId, corpusId, { force, fullRefresh }),
    onSettled: async (_data, _error, { corpusId }) => {
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCE_QUERY_KEY, clientId, corpusId] })
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
      await queryClient.invalidateQueries({
        queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, corpusId]
      })
    }
  })
}

export const useResyncFailedKnowledgeSourceDocuments = (
  clientId: number
): UseMutationResult<CorpusResponse, Error, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (corpusId) => await resyncFailedKnowledgeSourceDocuments(clientId, corpusId),
    onSettled: async (_data, _error, corpusId) => {
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCE_QUERY_KEY, clientId, corpusId] })
      await queryClient.invalidateQueries({ queryKey: [KNOWLEDGE_SOURCES_QUERY_KEY, clientId] })
      await queryClient.invalidateQueries({
        queryKey: [KNOWLEDGE_SOURCE_DOCUMENTS_QUERY_KEY, clientId, corpusId]
      })
    }
  })
}

export const useTeamworkSpaces = (clientId: number, enabled = true): UseQueryResult<TeamworkSpace[]> => {
  return useQuery({
    queryKey: [TEAMWORK_SPACES_QUERY_KEY, clientId],
    queryFn: async () => await listTeamworkSpaces(clientId),
    enabled: enabled && Boolean(clientId),
    staleTime: TEAMWORK_SPACES_STALE_MS,
    retry: shouldRetryTeamworkSpaces
  })
}
